import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { SelectField, TextField } from '@/components/ui/Field';
import { ClaimCodeInput } from '@/components/ui/ClaimCodeInput';
import { CLAIM_CODE_LENGTH } from '@/lib/claimCode';
import { claimDevice } from '@/api/endpoints/devices';
import { queryKeys } from '@/api/queryKeys';
import { ApiError } from '@/api/errors';
import type { Vehicle } from '@/types/api';

interface ClaimDeviceFormProps {
  vehicles: Vehicle[];
  onClaimed?: () => void;
}

/**
 * Claiming is how a device becomes yours. The backend deliberately answers every
 * failure — wrong uid, wrong code, wrong lifecycle state, locked out — with the
 * same "Unable to claim device", so that an attacker cannot use this form to
 * discover which device ids exist. That means this UI genuinely cannot tell the
 * user which part they got wrong, and must not pretend otherwise: it repeats the
 * server's message and points at the card the code is printed on.
 *
 * There is also a silent lockout after repeated failures. We do not advertise the
 * threshold (that would hand an attacker the budget), but after a few tries we stop
 * implying that trying harder will help and offer support instead.
 */
export function ClaimDeviceForm({ vehicles, onClaimed }: ClaimDeviceFormProps) {
  const queryClient = useQueryClient();
  const [deviceUid, setDeviceUid] = useState('');
  const [claimCode, setClaimCode] = useState('');
  const [vehicleId, setVehicleId] = useState<string>('');
  const [attempts, setAttempts] = useState(0);

  const mutation = useMutation({
    mutationFn: () =>
      claimDevice({
        device_uid: deviceUid.trim(),
        claim_code: claimCode,
        ...(vehicleId ? { vehicle_id: Number(vehicleId) } : {}),
      }),
    onSuccess: async () => {
      setAttempts(0);
      setDeviceUid('');
      setClaimCode('');
      await queryClient.invalidateQueries({ queryKey: queryKeys.devices });
      await queryClient.invalidateQueries({ queryKey: queryKeys.deviceStatus });
      await queryClient.invalidateQueries({ queryKey: queryKeys.summary });
      toast.success('Device linked. It will start reporting as soon as it powers on.');
      onClaimed?.();
    },
    onError: () => setAttempts((count) => count + 1),
  });

  const error = mutation.error;
  const isVehicleTaken =
    error instanceof ApiError && error.code === 'vehicle_already_has_a_device_attached';
  const ready = deviceUid.trim().length > 0 && claimCode.length === CLAIM_CODE_LENGTH;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <TextField
        label="Device ID"
        value={deviceUid}
        onChange={(event) => setDeviceUid(event.target.value)}
        placeholder="Printed beside the QR code"
        autoComplete="off"
        spellCheck={false}
        required
      />

      <ClaimCodeInput value={claimCode} onChange={setClaimCode} />

      <SelectField
        label="Attach to vehicle"
        value={vehicleId}
        onChange={(event) => setVehicleId(event.target.value)}
        hint="A vehicle can have one device at a time. You can attach it later."
      >
        <option value="">Not yet — just link it to my account</option>
        {vehicles.map((vehicle) => (
          <option key={vehicle.id} value={vehicle.id}>
            {vehicle.license_plate} · {vehicle.vehicle_type}
          </option>
        ))}
      </SelectField>

      {error && (
        <div className="rounded-[--radius-control] bg-bad-soft px-3 py-2.5 text-[13px] text-bad" role="alert">
          <p className="font-medium">
            {isVehicleTaken ? 'That vehicle already has a device' : "We couldn't link that device"}
          </p>
          <p className="mt-0.5 text-ink-secondary">
            {isVehicleTaken
              ? 'Remove the existing device from that vehicle first, or pick a different one.'
              : attempts >= 3
                ? 'Check the device ID and claim code against the card that came in the box. If they match, contact support — the device may already be registered or may need replacing.'
                : 'Check the device ID and claim code against the card that came in the box.'}
          </p>
        </div>
      )}

      <Button type="submit" variant="primary" loading={mutation.isPending} disabled={!ready}>
        Link device
      </Button>
    </form>
  );
}
